import {
    useDispatch,
    useEffect,
    useState,
    useNavigate,
    useParams,
    useSelector,
    titleToUrl,
} from '../../../imports/generalImport';
import { Box, Container, Button } from '../../../imports/muiImport';
import { setProductDetails, wipeOutProductDetails } from '../../../store/productInfo/action';
import { setNewDenomination, wipeOutDenomination } from '../../../store/productDetails/action';
import {
    createDenominationData,
    fetchProductById,
    isDenominationAvailable,
    isProductLive,
    productDetailApiCall,
    validateOutOfStock,
} from '../../../pages/PRODUCTDETAILS/productDetailHook';
import { CircularProgress } from '@mui/material';
import MobileProductDetailImage from '../../mobileComponents/mobileProductDetails/MobileProductDetailImage';
import MobileProductDetails from '../../mobileComponents/mobileProductDetails/MobileProductDetails';
import MobileGiftCardPurchase from '../../mobileComponents/mobileGiftCardPurchase/MobileGiftCardPurchase';
import ScrollToTop from '../../../components/other/ScrollToTop';
import { Helmet } from 'react-helmet-async';
import PageNotFound from '../../../pages/OTHERPAGE/PageNotFound';
import { getUniqueClass, isDiscountedProduct } from '../../../hooks/utilsFn/utilsFn';
import { getProductDetails } from '../../../apiCalls/urls/urlFunction';
import { actionNotifier } from '../../../components/tostify';

function MobileHomeProductDetails() {
    const [productDetail, setProductDetail] = useState({});
    const [productFound, setProductFound] = useState(false);
    const [cpId, setCPId] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);

    const { title: urlTitle, id: urlId } = useParams();
    const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
    const dispatch = useDispatch();

    const canonicalUrl = `https://www.99gift.ae/card-details/${urlId}/${titleToUrl(
        urlTitle
    )}`;

    // -------------------------------
    // PAGE SEO HEAD
    // -------------------------------
    const seoTitle = PRODUCTDETAILS?.name
        ? PRODUCTDETAILS?.name
        : `Buy ${PRODUCTDETAILS?.name} - 99gift.ae`;

    const seoDescription =
        PRODUCTDETAILS?.description ||
        `Get instant discounts on ${PRODUCTDETAILS?.name} when you purchase them online through 99gift. Take advantage of great deals on ${PRODUCTDETAILS?.name}, coupons, and promo codes.`;


    // -------------------------------
    // LOAD PRODUCT ON URL CHANGE
    // -------------------------------
    useEffect(() => {
        if (urlId !== cpId) {
            dispatch(wipeOutProductDetails());
            dispatch(wipeOutDenomination());
            getProductById(urlId);
            setCPId(urlId);
        }
    }, [urlId]);

    if (productFound) {
        return <PageNotFound />;
    }

    if (pageLoading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    // minWidth: "100%",
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Helmet>
                <title>{seoTitle}</title>

                <meta property="og:title" content={seoTitle} />
                <meta name="description" content={seoDescription} />
                <meta name="og:description" content={seoDescription} />

                <meta property="og:url" content={canonicalUrl} />
                <link rel="canonical" href={canonicalUrl} />

                {PRODUCTDETAILS?.metaSchema && (
                    <script type="application/ld+json">
                        {PRODUCTDETAILS?.metaSchema}
                    </script>
                )}

                <meta name="twitter:card" content="summary" />
                <meta
                    name="twitter:title"
                    content={PRODUCTDETAILS?.metaSEO?.metaTitle}
                />
                <meta
                    name="twitter:description"
                    content={PRODUCTDETAILS?.metaSEO?.metaDescription}
                />
                <meta name="twitter:image" content={PRODUCTDETAILS?.image} />
                <meta name="twitter:url" content={canonicalUrl} />
            </Helmet>

            <Container
                sx={{
                    minHeight: '100vh',
                    backgroundColor: 'white.main',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pt: 3,
                    mb: 10,
                }}
            >
                <ScrollToTop />
                <MobileProductDetailImage />
                <MobileProductDetails />

                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 90,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 1,
                        bgcolor: 'white.main',
                        zIndex: 1000,
                    }}
                >
                    {PRODUCTDETAILS?.outOfStock ? (
                        <Button
                            variant="contained"
                            fullWidth
                            className={`${getUniqueClass(PRODUCTDETAILS?.id, PRODUCTDETAILS?.title)}`}
                            sx={{
                                textTransform: 'capitalize',
                                bgcolor: 'error.main',
                                color: '#fff',
                                fontWeight: 'bold',
                                borderRadius: 0,
                                py: 1.5,
                                fontSize: '1.425rem',
                                boxShadow: 0,
                                '&:hover': {
                                    bgcolor: 'error.dark',
                                },
                            }}
                        >
                            Currently Out of Stock
                        </Button>
                    ) : (
                        <MobileGiftCardPurchase
                            showRangeDenomination={PRODUCTDETAILS?.rangeDenomination}
                        />
                    )}
                </Box>
            </Container>
        </>
    );

    async function getProductById(urlId) {
        setPageLoading(true);

        try {
            const response = await productDetailApiCall(urlId);
            if (!response?.status) {
                setProductFound(true);
                actionNotifier.error(response?.message);
                return;
            }

            if (!response?.data?.rangeDenomination) {
                dispatch(setNewDenomination(response?.data?.newDeno));
            }
            setProductDetail(response?.data);
            dispatch(setProductDetails(response?.data));
        } catch (err) {
            console.error(err);
        }
        finally {
            setPageLoading(false);
        }
    }
}

export default MobileHomeProductDetails;
